import React from "react";
import clsx from "clsx";
import { createUseStyles } from "react-jss";
import Linkify from "linkify-react";

import Document from "./Document";
import Gallery from "./Gallery";
import Video from "./Video";
import Image from "./Image";
import Audio from "./Audio";
import { getTheme } from "../Customisation";
import ReactQuill from "react-quill";

export const useStyles = createUseStyles(theme => ({
  content: {
    background: theme.popupSecondary,
    color: theme.textSecondary,
    textAlign: "left",
    boxSizing: "border-box",
    padding: [5, 20],
    marginTop: getTheme().popupSpace ?? 1,
    width: "100%",

    "& p": {
      lineHeight: "1.4em",
      margin: [10, 0],
      fontSize: 14,

      "& a": {
        color: theme.textHighlight,
      },
    },
  },
  quill: {
    "& .ql-container": {
      fontFamily: "inherit",
    },
    "& .ql-editor": {
      padding: 0,
    },
  },
  noteImage: {
    background: theme.popupSecondary,
    marginTop: getTheme().popupSpace ?? 1,
    width: "100%",
    cursor: "pointer",
  },
  lightbox: {
    background: theme.popupSecondary,
    width: "calc(100vw - 2px)",
    height: "calc(100vh - 43px)",
    marginTop: getTheme().popupSpace ?? 1,
    "& iframe": {
      backgroundColor: theme.popupSecondary,
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
    },
  },
  aspectRatio: {
    paddingTop: "min(56.25%, 100vh - 43px)",
    width: "100%",
    marginTop: getTheme().popupSpace ?? 1,
    position: "relative",
    "& iframe": {
      backgroundColor: theme.popupSecondary,
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
    },
  },
  pdf: {
    height: props => (props.large ? "calc(100vh - 43px)" : 520),
    width: "100%",
    marginTop: getTheme().popupSpace ?? 1,
    position: "relative",
    "@media (max-width: 600px)": {
      height: "calc(100vh - 43px)",
    },
    "& iframe": {
      backgroundColor: theme.popupSecondary,
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
    },
  },
  video: {
    width: "100%",
    marginTop: getTheme().popupSpace ?? 1,
    "& video": {
      width: "100%",
    },
  },
  fullscreen: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 30,
    height: 30,
    cursor: "pointer",
  },
  timeline: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    cursor: "pointer",
  },
  thumbnail: {
    border: `2px solid ${theme.textPrimary}`,
    width: 140,
    height: 75,
    margin: [5, 10, 5, 0],
    borderRadius: 3,
    backgroundSize: "600%",
    backgroundPosition: "center",
  },
  name: {
    fontWeight: "bold",
    margin: "0 !important",
  },
  date: {
    margin: "0 !important",
  },
  error: {
    extend: "content",
    textAlign: "center",
  },
  audio: {
    extend: "content",
    "& p": {
      margin: [5, 0],
      display: "flex",
      alignItems: "center",
    },
    "& img": {
      height: 15,
      width: 15,
      marginRight: 8,
    },
  },
  titleFormat: {
    "& p": {
      fontWeight: "bold",
      fontSize: 32,
    },
  },
  ycoTitle: {
    paddingBottom: 25,

    "&::after": {
      content: "''",
      background: theme.textHighlight,
      width: 60,
      height: 5,
      position: "absolute",
    },
  },
}));

export const fetchMedia = async uuid => {
  if (typeof uuid === "string") {
    let url = `https://manage.theatro360.com/api/v2/media/${uuid}`;
    if (typeof window !== "undefined" && window.useOffline === true) {
      url = `/api/v2/media/${uuid}/${window.token}`;
    }
    const res = await fetch(url);
    if (res.status >= 400) throw new Error(`fetch failed with ${res.status}`);
    const data = await res.json();
    return data;
  } else {
    throw new Error("UUID was not a string");
  }
};

const Content = ({ config, setExpandedImage, player, setAudioClip, large }) => {
  const classes = useStyles({ type: (config && config.type) || null, large });

  switch (config.type) {
    case "media":
      return config.options.elements.map(element => {
        switch (element.type) {
          case "text":
            if (
              !element.format ||
              element.format === "plaintext" ||
              element.format === "title"
            ) {
              if (!element.content) return null;
              return (
                <div
                  className={clsx(classes.content, {
                    [classes.titleFormat]: element.format === "title",
                    [classes.ycoTitle]:
                      element.format === "title" &&
                      window.location.host.split(".")[0] === "yco",
                  })}
                  key={element.id}
                >
                  {element.content.split("\n").map(
                    (line, i) =>
                      line && (
                        <Linkify
                          key={`${element.title}-${i}`}
                          tagName="p"
                          style={{ fontSize: element.fontSize }}
                        >
                          {line}
                        </Linkify>
                      ),
                  )}
                </div>
              );
            } else if (element.format === "richtext") {
              return (
                <div className={classes.content}>
                  <ReactQuill
                    className={classes.quill}
                    theme={null}
                    value={element.content}
                    readOnly
                  />
                </div>
              );
            } else {
              console.warn("this version does not support this text format");
              return null;
            }
          case "image":
            return (
              <Image
                key={element.id}
                {...element.image}
                config={config}
                setExpandedImage={setExpandedImage}
              />
            );
          case "link":
            return (
              <div key={element.id} className={classes.aspectRatio}>
                <iframe
                  frameBorder="0"
                  title="Web Lightbox"
                  src={element.url}
                />
              </div>
            );
          case "video":
            return (
              <Video
                key={element.id}
                video_type={element.video_type}
                url={element.url}
                video={element.video}
                session={player.params.get("shared_tour_id")}
              />
            );
          case "audio":
            return (
              <Audio
                key={element.id}
                id={element.clip.id}
                name={element.clip.name}
                autoplay={element.autoplay}
                setAudioClip={setAudioClip}
              />
            );
          case "document":
            return (
              <Document
                key={element.id}
                id={element.document.id}
                title={element.document.name}
                session={player.params.get("shared_tour_id")}
                large={large}
              />
            );
          default:
            console.info(`unknown element type "${element.type}"`);
            return null;
        }
      });
    case "note":
      if (config.options.content || config.options.image) {
        return (
          <>
            {config.options.image && (
              <img
                className={classes.noteImage}
                src={config.options.image}
                alt=""
                onClick={evt =>
                  void (evt.stopPropagation(),
                  setExpandedImage({
                    config: {
                      type: "gallery",
                      options: {
                        title: config.options.title,
                        content: [config.options.image],
                      },
                    },
                    prev: config,
                  }))
                }
              />
            )}
            {config.options.audio && (
              <Audio
                name={config.options.title || config.tooltip}
                autoplay={true}
                url={config.options.audio}
                setAudioClip={setAudioClip}
              />
            )}
            {config.options.content && (
              <div className={classes.content}>
                {config.options.content.split("\n").map(
                  (line, i) =>
                    line && (
                      <Linkify key={`${config.options.title}-${i}`} tagName="p">
                        {line}
                      </Linkify>
                    ),
                )}
              </div>
            )}
          </>
        );
      } else {
        return null;
      }
    case "web":
      return (
        <iframe
          frameBorder="0"
          className={classes.lightbox}
          title="Web Lightbox"
          src={config.options.url}
        />
      );
    case "gallery":
      return <Gallery config={config} />;
    case "video":
      return (
        <Video
          video_type={config.options.video_type}
          url={config.options.url}
          large={config.options.large}
        />
      );
    case "audio":
      return config.options.content ? (
        <>
          <Audio
            name={config.options.title || config.tooltip}
            autoplay={true}
            url={config.options.url}
            setAudioClip={setAudioClip}
          />
          <div className={classes.content}>
            {config.options.content.split("\n").map(
              (line, i) =>
                line && (
                  <Linkify key={`${config.options.title}-${i}`} tagName="p">
                    {line}
                  </Linkify>
                ),
            )}
          </div>
        </>
      ) : null;
    case "timeline":
      return config.options.scenes ? (
        <div className={classes.content} style={{ padding: "5px 10px" }}>
          {config.options.scenes.map(({ id, scene, name, date = "" }) => (
            <div
              className={classes.timeline}
              onClick={() => {
                player.transitionTo(scene, undefined, "fade");
              }}
              key={id}
            >
              <div
                className={classes.thumbnail}
                style={
                  scene !== player.getActiveImage()
                    ? {
                        borderColor: "transparent",
                        backgroundImage: `url("${player.config.images[scene]?.urls.low}")`,
                      }
                    : {
                        backgroundImage: `url("${player.config.images[scene]?.urls.low}")`,
                      }
                }
              />
              <div>
                <p className={classes.name}>{name}</p>
                {date && <p className={classes.date}>{date}</p>}
              </div>
            </div>
          ))}
        </div>
      ) : null;
    default:
      return null;
  }
};

export default Content;
